import React, { useEffect, useMemo, useState } from 'react';
import axios from '../../services/axios';
import toast from 'react-hot-toast';
import { UserPlusIcon } from '@heroicons/react/24/outline';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';

const ROLE_OPTIONS = [
    { value: 'program_manager', label: 'Program Manager (PM)' },
    { value: 'mentor', label: 'Mentor' },
    { value: 'trainer', label: 'Trainer' },
    { value: 'super_admin', label: 'Super Admin' }
];

const emptyForm = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    roleName: 'program_manager'
};

function roleNames(u) {
    return (u.Roles || u.roles || []).map((r) => (typeof r === 'string' ? r : r.name));
}

export default function AdminStaff() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState(emptyForm);

    const loadUsers = async () => {
        const res = await axios.get('/users', { params: { page: 1, limit: 200 } });
        setUsers(res.data.data?.users || []);
    };

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                await loadUsers();
            } catch (err) {
                toast.error(err.response?.data?.message || 'Unable to load staff');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const staff = useMemo(() => {
        const staffRoles = new Set(['program_manager', 'mentor', 'trainer', 'super_admin']);
        return users.filter((u) => roleNames(u).some((r) => staffRoles.has(r)));
    }, [users]);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await axios.post('/users', {
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                email: form.email.trim(),
                phone: form.phone.trim() || undefined,
                password: form.password,
                roleName: form.roleName
            });
            toast.success('Account created — they can log in with this email and password');
            setForm(emptyForm);
            await loadUsers();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Unable to create account');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="w-full space-y-6">
            <PageHeader
                title="Staff accounts"
                description="Create Program Manager, Mentor, Trainer, or Admin logins. Then assign mentors to cohorts."
            />

            <form onSubmit={submit} className="card max-w-2xl space-y-4">
                <div className="flex items-center gap-2">
                    <UserPlusIcon className="h-5 w-5 text-brand-700" aria-hidden="true" />
                    <h2 className="text-base font-semibold text-ink-950">Create staff login</h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="label" htmlFor="staff-first">First name *</label>
                        <input id="staff-first" className="input-field" value={form.firstName} onChange={set('firstName')} required />
                    </div>
                    <div>
                        <label className="label" htmlFor="staff-last">Last name *</label>
                        <input id="staff-last" className="input-field" value={form.lastName} onChange={set('lastName')} required />
                    </div>
                </div>

                <div>
                    <label className="label" htmlFor="staff-email">Email *</label>
                    <input id="staff-email" type="email" className="input-field" value={form.email} onChange={set('email')} required />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="label" htmlFor="staff-phone">Phone</label>
                        <input id="staff-phone" className="input-field" value={form.phone} onChange={set('phone')} />
                    </div>
                    <div>
                        <label className="label" htmlFor="staff-role">Role *</label>
                        <select id="staff-role" className="input-field" value={form.roleName} onChange={set('roleName')}>
                            {ROLE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div>
                    <label className="label" htmlFor="staff-password">Temporary password *</label>
                    <input
                        id="staff-password"
                        type="text"
                        className="input-field"
                        value={form.password}
                        onChange={set('password')}
                        minLength={8}
                        placeholder="Min. 8 characters"
                        required
                    />
                    <p className="mt-1 text-xs text-ink-500">Share this password securely; they can change it after login.</p>
                </div>

                <div className="flex justify-end">
                    <button type="submit" className="btn-primary" disabled={busy}>
                        {busy ? 'Creating…' : 'Create account'}
                    </button>
                </div>
            </form>

            <section className="space-y-3">
                <h2 className="text-base font-semibold text-ink-950">Existing staff</h2>
                {loading ? (
                    <div className="skeleton h-24" role="status" />
                ) : (
                    <div className="overflow-x-auto rounded-lg border border-border">
                        <table className="min-w-full text-sm">
                            <thead className="bg-ink-50 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
                                <tr>
                                    <th className="px-3 py-2">Name</th>
                                    <th className="px-3 py-2">Email</th>
                                    <th className="px-3 py-2">Role</th>
                                    <th className="px-3 py-2">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {staff.map((u) => (
                                    <tr key={u.id} className="text-ink-800">
                                        <td className="px-3 py-2 font-medium">
                                            {[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}
                                        </td>
                                        <td className="px-3 py-2 text-ink-600">{u.email}</td>
                                        <td className="px-3 py-2">
                                            {roleNames(u).map((r) => (
                                                <span key={r} className="badge badge-neutral mr-1">{r.replace(/_/g, ' ')}</span>
                                            ))}
                                        </td>
                                        <td className="px-3 py-2">
                                            <StatusBadge status={u.is_active ? 'active' : 'inactive'} />
                                        </td>
                                    </tr>
                                ))}
                                {staff.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="px-3 py-6 text-center text-ink-500">
                                            No staff accounts yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}
